import React, { useMemo } from 'react'
import { IonItem, IonLabel, IonRow, IonToggle, IonGrid, IonContent, IonList, IonItemDivider } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { getAllPNNVOptionsSelector, getVitalOptionsSelector } from '../../../options/selectors'
import { IRootState } from '../../../../store'
import {
  setWithdrawalScaleType,
  setWithdrawalScale,
  setWithdrawalScaleTotal,
  setWithdrawalScaleTotalPNNV,
  setWithdrawalScaleTotalPNNVEnabled,
  setWithdrawalScaleTypeCleared,
  saveVitals,
} from '../../store/vitals.actions'
import { IOption, IPNNVOption } from '../../../../common/common.types'
import { IWithdrawalScale, IWithdrawalScaleSection } from '../../../options/store/options.types'
import './WithdrawalScale.content.css'

const WithdrawalScaleContent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const {
    withdrawalScaleCriteria,
    withdrawalScaleType,
    withdrawalScaleTotal,
    withdrawalScaleTotalPNNV,
    __isWithdrawalScaleTotalPNNVEnabled,
    __isWithdrawalScaleTypeCleared,
  } = useSelector((state: IRootState) => state.vitals)

  const vitalOptions = useSelector(getVitalOptionsSelector)
  const { withdrawalScaleOptions, withdrawScaleTypeOptions } = vitalOptions

  const allPNNVOptions = useSelector(getAllPNNVOptionsSelector)
  const withdrawalScalePNNVOptions = allPNNVOptions?.['eVitals.913'] || []

  const selections = useMemo(() => {
    return withdrawalScaleCriteria ?? []
  }, [withdrawalScaleCriteria])

  const displayWithdrawalScaleType = useMemo((): IOption | null => {
    if (withdrawalScaleType) {
      return withdrawalScaleType
    }
    if (__isWithdrawalScaleTypeCleared) {
      return null
    }
    return withdrawScaleTypeOptions?.[0] ?? null
  }, [withdrawalScaleType, __isWithdrawalScaleTypeCleared, withdrawScaleTypeOptions])

  const selectedScaleDefinition: IWithdrawalScale | undefined = withdrawalScaleOptions?.find(
    (scale: IWithdrawalScale) => scale.scaleId === displayWithdrawalScaleType?.id
  )

  const selectionMap = useMemo(() => {
    const map = new Map<number | undefined, IOption>()
    selections.forEach((sel: IOption) => {
      if (sel.fieldId) {
        map.set(sel.fieldId, sel)
      }
    })
    return map
  }, [selections])

  const displayTotal = withdrawalScaleTotalPNNV ? null : withdrawalScaleTotal

  const persistDefaultWithdrawalScaleType = () => {
    if (!withdrawalScaleType && !__isWithdrawalScaleTypeCleared && withdrawScaleTypeOptions?.[0]) {
      dispatch(setWithdrawalScaleType(withdrawScaleTypeOptions[0]))
    }
  }

  const handleSetWithdrawalScaleType = (option: IOption | null) => {
    dispatch(setWithdrawalScale([]))
    dispatch(setWithdrawalScaleTotal(null))
    dispatch(setWithdrawalScaleTotalPNNV(null))
    dispatch(setWithdrawalScaleTotalPNNVEnabled(false))
    dispatch(setWithdrawalScaleType(option))
    dispatch(setWithdrawalScaleTypeCleared(!option))
  }

  const handleSectionSelection = (option: IOption | null, section: IWithdrawalScaleSection) => {
    const baseSelections = (withdrawalScaleCriteria ?? []).filter((sel) => sel.fieldId !== section.fieldId)

    let updatedSelections = baseSelections
    if (option) {
      const selection = { ...option, question: section.name, element: section.nemsisElement, fieldId: section.fieldId }
      updatedSelections = [...baseSelections, selection]
    }

    if (updatedSelections.length > 0) {
      persistDefaultWithdrawalScaleType()
    }

    dispatch(setWithdrawalScale(updatedSelections))

    const totalSections = selectedScaleDefinition?.sections?.length ?? 0

    if (updatedSelections.length === totalSections) {
      const totalScore = updatedSelections.reduce((sum, opt) => sum + (opt.score ?? 0), 0)
      if (!withdrawalScaleTotalPNNV) dispatch(setWithdrawalScaleTotal(totalScore.toString()))
    } else {
      dispatch(setWithdrawalScaleTotal(null))
    }

    dispatch(saveVitals())
  }

  const setWithdrawalScaleTotalPNNVOnChange = (option: IPNNVOption | null) => {
    if (option) {
      persistDefaultWithdrawalScaleType()
    }
    dispatch(setWithdrawalScaleTotalPNNV(option))
    dispatch(setWithdrawalScaleTotal(null))
    dispatch(saveVitals())
  }

  const setTotalReasonEnabledonChange = () => {
    const enabling = !__isWithdrawalScaleTotalPNNVEnabled
    if (enabling) {
      persistDefaultWithdrawalScaleType()
    }
    dispatch(setWithdrawalScaleTotalPNNVEnabled(enabling))
    dispatch(setWithdrawalScaleTotalPNNV(null))
    dispatch(setWithdrawalScaleTotal(null))
    dispatch(saveVitals())
  }

  return (
    <IonContent className='ion-margin-bottom' fullscreen>
      <IonItemDivider color='light' className='grid-item-divider-container'></IonItemDivider>
      <IonList>
        <IonGrid className='ion-no-padding'>
          {displayWithdrawalScaleType && (
            <>
              <IonRow className=' mt-4 pb-10'>
                <IonRow className='w-100 flex ion-justify-content-between'>
                  <div className='m-8-20'>
                    <IonLabel>{t('vitals.withdrawalScale.total')}</IonLabel>
                    <IonLabel className='custom-number-label'>{displayTotal || 'N/A'}</IonLabel>
                  </div>
                  <IonItem lines='none'>
                    <IonToggle
                      onClick={setTotalReasonEnabledonChange}
                      color='zoll-blue-secondary'
                      checked={__isWithdrawalScaleTotalPNNVEnabled}
                    />
                    <IonLabel className='m-5' color='medium'>
                      {t('vitals.withdrawalScale.notDone')}
                    </IonLabel>
                  </IonItem>
                </IonRow>

                {__isWithdrawalScaleTotalPNNVEnabled && (
                  <>
                    <SingleSelect
                      dataTestID='WithdrawalScaleTotalReason'
                      options={withdrawalScalePNNVOptions}
                      listHeader={t('vitals.withdrawalScale.reasonNotDone')}
                      label={t('vitals.withdrawalScale.reasonNotDone')}
                      pnnvAction={setWithdrawalScaleTotalPNNVOnChange}
                      action={() => undefined}
                      selectedPNNVOption={withdrawalScaleTotalPNNV}
                      showPNNVAsNormals={true}
                    />
                    <hr className='field-divider' />
                  </>
                )}
              </IonRow>
              <IonItemDivider color='light' className='grid-item-divider-container'></IonItemDivider>
            </>
          )}

          <SingleSelect
            dataTestID='WithdrawalScaleType'
            options={withdrawScaleTypeOptions}
            selectedOption={displayWithdrawalScaleType}
            listHeader={t('vitals.withdrawalScale.withdrawalScaleType')}
            label={t('vitals.withdrawalScale.withdrawalScaleType')}
            action={handleSetWithdrawalScaleType}
          />
          <hr className='field-divider' />
          {selectedScaleDefinition?.sections.length &&
            selectedScaleDefinition?.sections?.map((section: IWithdrawalScaleSection, index: number) => {
              const selectedOption = selectionMap.get(section.fieldId) ?? null

              return (
                <React.Fragment key={`${section.nemsisElement}_${section.name}`}>
                  <SingleSelect
                    dataTestID={`WithdrawalScale_${selectedScaleDefinition.scaleId}_${section.name}`}
                    options={section.options}
                    selectedOption={selectedOption}
                    listHeader={section.name}
                    label={section.name}
                    showSelectedOptionId='score'
                    action={(option) => handleSectionSelection(option, section)}
                  />
                  {index < selectedScaleDefinition?.sections?.length - 1 && <hr className='field-divider' />}
                </React.Fragment>
              )
            })}
        </IonGrid>
      </IonList>
    </IonContent>
  )
}

export default WithdrawalScaleContent
